import { computed, ref, type Ref } from 'vue'
import { useAuthStore } from '@/presentation/stores/auth'

/**
 * Composable for dashboard performance optimizations
 * Provides common optimizations for dashboard components
 */
export function useDashboardPerformance() {
  // Auth info to be provided to child components
  const authStore = useAuthStore()
  const authInfo = computed(() => ({
    isAdmin: authStore.isAdmin,
    isEduEngineer: authStore.isEduEngineer
  }))

  // Debounced filters for tables
  const createDebouncedFilter = (initialValue: string | null = null, delay = 300) => {
    const value = ref(initialValue)
    const debouncedValue = ref(initialValue)
    let timeout: NodeJS.Timeout

    const updateDebouncedValue = (newValue: string | null) => {
      value.value = newValue
      clearTimeout(timeout)
      timeout = setTimeout(() => {
        debouncedValue.value = newValue
      }, delay)
    }

    return {
      value,
      debouncedValue,
      updateDebouncedValue
    }
  }

  // Memoized chart options to prevent recreation
  const createOptimizedChartOptions = (customOptions: any = {}) => {
    return computed(() => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: {
        duration: 0 // Disable animations for better performance
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          enabled: true,
          animation: false,
          ...customOptions.plugins?.tooltip
        }
      },
      scales: {
        x: {
          ticks: {
            maxTicksLimit: 10 // Limit number of ticks for performance
          }
        },
        y: {
          beginAtZero: true,
          ticks: {
            maxTicksLimit: 8
          }
        }
      },
      ...customOptions
    }))
  }

  // Progress indicator calculator (moved from inline templates)
  const calculateProgressIndicator = (
    variation: number | null | undefined,
    endScore: number | null | undefined,
    startScore: number | null | undefined
  ): string => {
    if (variation !== null && variation !== undefined) {
      return variation > 0 ? '+' : variation < 0 ? '-' : '='
    }
    if (
      endScore !== null &&
      endScore !== undefined &&
      startScore !== null &&
      startScore !== undefined
    ) {
      return endScore > startScore ? '+' : endScore < startScore ? '-' : '='
    }
    return '='
  }

  // Virtualization helper for large lists
  const createVirtualizedList = <T>(items: Ref<T[]>, itemHeight = 46) => {
    const visibleRange = ref({ start: 0, end: 20 })

    const visibleItems = computed(() => {
      return items.value.slice(visibleRange.value.start, visibleRange.value.end)
    })

    const updateVisibleRange = (start: number, end: number) => {
      visibleRange.value = { start, end }
    }

    return {
      visibleItems,
      visibleRange,
      updateVisibleRange,
      totalHeight: computed(() => items.value.length * itemHeight)
    }
  }

  return {
    authInfo,
    createDebouncedFilter,
    createOptimizedChartOptions,
    calculateProgressIndicator,
    createVirtualizedList
  }
}
